/* eslint-disable */
import React from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import PublicSeoPage from '../../components/shared/PublicSeoPage';
import PageMeta from '../../components/shared/PageMeta';
import SeoConversionCTA from '../../components/shared/SeoConversionCTA';

// The Earn surface. The hive pays the people who power it: 90 percent of every
// paid call flows to the compute providers, not a corporation. This page states
// the stable earning mechanics (the 90/9/1 split, compute lending for Spark,
// seeding an endpoint, the student path) and links /pricing for the live tiers
// rather than hard-coding dollar amounts that could go stale. Static content, so
// it prerenders cleanly. No em dashes in any rendered string.

const PATHS = [
  {
    tag: 'Lend compute',
    title: 'Run a node, earn Spark',
    body: 'Point your spare GPU or CPU hours at the hive and earn Spark for the work you contribute. Rewards are log-scaled and cause-aligned, so honest hosting compounds. Ninety percent of paid API revenue flows to the providers who serve it.',
    to: 'https://github.com/hertz-ai',
    cta: 'Run a node on GitHub',
    external: true,
    color: '#17c07f',
  },
  {
    tag: 'Seed an endpoint',
    title: 'Put your model on the hive',
    body: 'Expose a model or an agent through the intelligence API and earn from real usage. There is a free tier for people to try it and paid tiers that route the same 90 percent back to you, the provider.',
    to: '/pricing',
    cta: 'See the API and tiers',
    color: '#7c72ff',
  },
  {
    tag: 'For students',
    title: 'Earn while you learn',
    body: 'You do not need a data center. Contribute spare compute, run experiments, build a visible track record on the hive, and get paid in Spark for the value you add. A real on-ramp, not an internship you pay for.',
    to: '/social',
    cta: 'Join the community',
    color: '#34d3ff',
  },
];

const SPLIT = [
  { pct: '90%', who: 'Compute providers', note: 'The people who actually serve the intelligence. Log-scaled, cause-aligned.' },
  { pct: '9%', who: 'The network', note: 'Keeping the hive running: routing, storage, the shared infrastructure.' },
  { pct: '1%', who: 'Causes and governance', note: 'A dividend to the commons and the votes that steer it.' },
];

export default function EarnPage() {
  return (
    <>
      <PageMeta
        title="Earn from the Hive: Lend Compute, Seed an Endpoint, Get Paid in Spark | Hevolve"
        description="Earn from Hevolve's hive: lend spare compute for Spark, seed a model or agent on the intelligence API, or start as a student. Ninety percent of paid usage flows to the providers who serve it. Local-first, democratic intelligence."
        ogTitle="Earn from the Hive: compute, endpoints, and Spark"
        ogDescription="Ninety percent of paid usage flows to the people who power the hive. Lend compute, seed an endpoint, or start as a student."
        path="/earn"
      >
        <script type="application/ld+json">{JSON.stringify({ '@context': 'https://schema.org', '@type': 'WebPage', name: 'Earn from the Hive', url: 'https://hevolve.ai/earn', description: 'Lend compute, seed an endpoint, or start as a student and earn Spark. Ninety percent of paid usage flows to compute providers.', publisher: { '@type': 'Organization', name: 'Hevolve AI', url: 'https://hevolve.ai' } })}</script>
      </PageMeta>
      <PublicSeoPage
        heading="Earn from the hive"
        subheading="The hive pays the people who power it. Ninety percent of every paid call flows to the compute providers, not a corporation."
        maxWidth="lg"
      >
        <Grid container spacing={2.5} sx={{ mt: 1, mb: 5 }}>
          {PATHS.map((p) => (
            <Grid item xs={12} md={4} key={p.title}>
              <Box sx={{ height: '100%', p: 3, borderRadius: 3, display: 'flex', flexDirection: 'column', gap: 1.25,
                background: 'rgba(255,255,255,0.03)', border: `1px solid ${p.color}44`,
                transition: 'transform .16s, border-color .2s',
                '&:hover': { transform: 'translateY(-3px)', borderColor: `${p.color}` } }}>
                <Typography sx={{ fontFamily: 'monospace', fontSize: '0.7rem', letterSpacing: 1, textTransform: 'uppercase', fontWeight: 700, color: p.color }}>
                  {p.tag}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff' }}>{p.title}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7 }}>{p.body}</Typography>
                {p.external ? (
                  <Typography component="a" href={p.to} target="_blank" rel="noopener noreferrer"
                    sx={{ mt: 'auto', fontWeight: 700, fontSize: '0.9rem', color: p.color, textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                    {p.cta} &rarr;
                  </Typography>
                ) : (
                  <Typography component={Link} to={p.to}
                    sx={{ mt: 'auto', fontWeight: 700, fontSize: '0.9rem', color: p.color, textDecoration: 'none', '&:hover': { color: '#fff' } }}>
                    {p.cta} &rarr;
                  </Typography>
                )}
              </Box>
            </Grid>
          ))}
        </Grid>

        <Typography variant="h5" sx={{ fontWeight: 800, color: '#fff', mb: 0.5 }}>Where the money goes: 90 / 9 / 1</Typography>
        <Typography variant="body1" sx={{ color: 'rgba(255,255,255,0.7)', mb: 2.5, maxWidth: 760 }}>
          Every paid call is split the same way, in the open. The people who serve the intelligence keep the overwhelming majority.
        </Typography>
        <Grid container spacing={2} sx={{ mb: 5 }}>
          {SPLIT.map((s, i) => (
            <Grid item xs={12} sm={4} key={s.who}>
              <Box sx={{ p: 3, borderRadius: 3, bgcolor: i === 0 ? 'rgba(23,192,127,0.08)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', height: '100%' }}>
                <Typography variant="h3" sx={{ fontWeight: 800, color: i === 0 ? '#17c07f' : '#cfcaff' }}>{s.pct}</Typography>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#fff', mt: 0.5 }}>{s.who}</Typography>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.65)', mt: 0.5 }}>{s.note}</Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Box sx={{ p: 3, borderRadius: 3, bgcolor: 'rgba(124,114,255,0.06)', border: '1px solid rgba(124,114,255,0.2)', mb: 5 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>How you get paid</Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.74)', lineHeight: 1.7, maxWidth: 760 }}>
            Earnings accrue as Spark in your wallet, traceable to the compute you contributed and the usage you served. Cash out runs on Stripe for cards worldwide and PhonePe for UPI in India. The formula is public, and every number traces back to a source you can check, which is the whole point of a democratic hive.
          </Typography>
          <Stack direction="row" spacing={2} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
            <Button component={Link} to="/docs" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#7c72ff' } }}>Read the revenue model</Button>
            <Button component={Link} to="/pricing" variant="outlined" sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.3)', textTransform: 'none', fontWeight: 600, '&:hover': { borderColor: '#7c72ff' } }}>See the tiers</Button>
          </Stack>
        </Box>

        <SeoConversionCTA
          channel="earn"
          headline="Start by running the hive on your own machine"
          subhead="Earning starts with a node. Install Nunba or HART OS, contribute what you have, and get paid in Spark for the value you add."
          primaryLabel="Get Nunba"
          primaryTo="/download"
          secondaryLabel="Explore the agents"
          secondaryTo="/agents"
        />
      </PublicSeoPage>
    </>
  );
}
